import {
  Error,
  Paging,
} from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { SummaryInfo } from "app/based/baseApiModel/baseApiResponseModel/ListBaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import dayjs from "dayjs";
import { AirTimeRegistrationRequestModel } from "features/airTimeRegistration/data/model/AirTimeRegistrationRequestModel";
import { AirTimeRegistrationResponseModel } from "features/airTimeRegistration/data/model/AirTimeRegistrationResponseModel";
import { AirtimeRegistrationInjection } from "features/airTimeRegistration/di/AirTimeRegistrationInjection";
import {
  ExportPayload,
  FetchListInput,
} from "features/airTimeRegistration/domain/entities/AirTimeRegistrationPayload";
import { DenominationListRequestEntity } from "features/airTimeRegistration/domain/entities/DenominationListRequestEntity";
import { PartnerListResponseEntity } from "features/airTimeRegistration/domain/entities/PartnerListResponseEntity";
import { StoreListRequestEntiy } from "features/airTimeRegistration/domain/entities/StoreListRequestEntity";
import { StoreListResponseEntity } from "features/airTimeRegistration/domain/entities/StoreListResponseEntity";
import isEmpty from "lodash/isEmpty";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { getMessage } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";

export interface IDropdownActionRecord {
  record: AirTimeRegistrationResponseModel;
  action: string;
}

export type SavedDetailActionType =
  AirTimeRegistrationResponseModel | IDropdownActionRecord;

class AirTimeRegistrationInput implements Disposable {
  public loading = new BehaviorSubject<boolean>(false);
  public detailLoading = new BehaviorSubject<boolean>(false);
  public statusLoading = new BehaviorSubject<Record<string, boolean>>({});
  public fetchList = new BehaviorSubject<FetchListInput>({
    kind: "params",
    params: new AirTimeRegistrationRequestModel(),
  });
  public fetchCarrier = new BehaviorSubject<BaseFilterListRequestModel>({});
  public exportExcel = new BehaviorSubject<ExportPayload | null>(null);
  public fetchDetail = new BehaviorSubject<AirTimeRegistrationRequestModel>(
    new AirTimeRegistrationRequestModel(),
  );
  public fetchPartners = new BehaviorSubject<BaseFilterListRequestModel>({});
  public loadStores = new BehaviorSubject<StoreListRequestEntiy>(
    new StoreListRequestEntiy(),
  );
  public loadStaff = new BehaviorSubject<BaseUserRequestModel>({});
  public loadUsers = new BehaviorSubject<BaseUserRequestModel>({});
  public fetchDenomination = new BehaviorSubject<DenominationListRequestEntity>(
    new DenominationListRequestEntity(),
  );

  [Symbol.dispose](): void {
    this.loading.complete();
    this.detailLoading.complete();
    this.fetchList.complete();
    this.exportExcel.complete();
    this.fetchDetail.complete();
    this.loadStores.complete();
    this.loadStaff.complete();
    this.fetchPartners.complete();
    this.loadUsers.complete();
    this.fetchDenomination.complete();
  }
}

class AirTimeRegistrationOutput implements Disposable {
  public savedList = new BehaviorSubject<
    Array<AirTimeRegistrationResponseModel>
  >([]);
  public savedCarrier = new BehaviorSubject<Array<BaseFilterListResponseModel>>(
    [],
  );
  public savedDetail = new BehaviorSubject<SavedDetailActionType>(
    {} as AirTimeRegistrationResponseModel,
  );
  public pagination = new BehaviorSubject<Paging>({});
  public onError = new BehaviorSubject<Error>({});
  public onExportSuccess = new BehaviorSubject<boolean>(false);
  public onCheckStatusSuccess = new BehaviorSubject<boolean>(false);
  public savedPartners = new BehaviorSubject<Array<PartnerListResponseEntity>>(
    [],
  );
  public savedSummary = new BehaviorSubject<SummaryInfo>({});
  public savedStores = new BehaviorSubject<Array<StoreListResponseEntity>>([]);
  public savedStaff = new BehaviorSubject<Array<BaseUserResponseModel>>([]);
  public savedUsers = new BehaviorSubject<Array<BaseUserResponseModel>>([]);
  public savedDenominationList = new BehaviorSubject<
    Array<BaseFilterListResponseModel>
  >([]);
  public savedQueryParams =
    new BehaviorSubject<AirTimeRegistrationRequestModel | null>(null);

  [Symbol.dispose](): void {
    this.savedList.complete();
    this.savedCarrier.complete();
    this.savedDetail.complete();
    this.pagination.complete();
    this.onError.complete();
    this.onExportSuccess.complete();
    this.onCheckStatusSuccess.complete();
    this.savedSummary.complete();
    this.savedPartners.complete();
    this.savedStores.complete();
    this.savedStaff.complete();
    this.savedUsers.complete();
    this.savedDenominationList.complete();
    this.savedQueryParams.complete();
  }
}

@injectable()
export class AirTimeRegistrationViewModel extends BaseViewModel<
  AirTimeRegistrationInput,
  AirTimeRegistrationOutput
> {
  constructor() {
    super(new AirTimeRegistrationInput(), new AirTimeRegistrationOutput());
  }

  private airTimeRegistrationUseCase =
    AirtimeRegistrationInjection.getAirTimeRegistrationUseCase();

  private downloadExcel(data: Blob) {
    const url = window.URL.createObjectURL(data as Blob);
    const link = document.createElement("a");
    const timestamp = dayjs().format("YYYYMMDD_HHmmss");
    link.href = url;
    link.setAttribute("download", `AIR_TIME_REPORT_${timestamp}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  protected override transform(): void {
    this.skipFirstInitial(
      this.input.fetchList,
      async (input: FetchListInput) => {
        try {
          this.input.loading.next(true);
          let res;

          let paramsToSave: AirTimeRegistrationRequestModel | null = null;

          switch (input.kind) {
            case "search": {
              const result =
                await this.airTimeRegistrationUseCase.getListFromSearch(
                  input.rawFormValues,
                  input.context,
                );
              res = result.list;
              paramsToSave = result.params;
              break;
            }
            case "tableChange": {
              const result =
                await this.airTimeRegistrationUseCase.getListWithTableChange(
                  input.prevParams,
                  input.rawFormValues,
                  input.page,
                  input.limit,
                );
              res = result.list;
              paramsToSave = result.params;
              break;
            }
            case "redo": {
              const redoResult =
                await this.airTimeRegistrationUseCase.getListRedo();
              res = redoResult.list;
              paramsToSave = redoResult.params;
              break;
            }
            case "params":
            default:
              res = await this.airTimeRegistrationUseCase.getList(input.params);
              break;
          }

          if (paramsToSave) {
            this.output.savedQueryParams.next(paramsToSave);
          }

          if (res?.data) {
            this.output.savedList.next(res?.data ?? []);
            this.output.pagination.next(res?.pagination);
            this.output.savedSummary.next(res?.summary);
          }
        } catch (error) {
          console.error(error);
          this.output.onError.next(error);
          this.output.savedList.next([]);
        } finally {
          this.input.loading.next(false);
        }
      },
    );

    this.skipFirstInitial(this.input.fetchCarrier, async (params) => {
      try {
        const res = await this.airTimeRegistrationUseCase.getFilterList(
          params,
          "carrier",
        );
        if (res) {
          this.output.savedCarrier.next(res);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      try {
        this.input.detailLoading.next(true);
        const res = await this.airTimeRegistrationUseCase.getDetail(params);
        if (res?.data) {
          this.output.savedDetail.next(res?.data);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.detailLoading.next(false);
      }
    });

    this.skipFirstInitial(this.input.exportExcel, async (payload) => {
      if (!payload) return;
      try {
        this.input.loading.next(true);
        const res =
          await this.airTimeRegistrationUseCase.exportReportFromSearch(payload);
        if (res?.data) {
          if (res?.data?.size < 1000) {
            getMessage(strings.GlobalMessage.ExcelError, 3, "error");
            return;
          } else {
            this.downloadExcel(res?.data);
            this.output.onExportSuccess.next(true);
          }
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });

    this.skipFirstInitial(this.input.fetchPartners, async (params) => {
      try {
        const cache = productCache.getCache(CacheKeyEnum.partnerList);

        if (!isEmpty(cache)) {
          this.output.savedPartners.next(cache);
          return;
        }
        const res = await this.airTimeRegistrationUseCase.getFilterList(
          params,
          "partner",
        );
        const data = res ?? [];

        this.output.savedPartners.next(data);

        productCache.saveCache({
          key: CacheKeyEnum.partnerList,
          product: data,
        });
      } catch (error) {
        console.error("Error fetching partners:", error);
        this.output.savedPartners.next([]);
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.loadStores, async (params) => {
      const { partnerCode } = params;
      try {
        if (partnerCode) {
          const res = await this.airTimeRegistrationUseCase.getFilterList(
            params,
            "store",
          );
          this.output.savedStores.next(res);
          return;
        }
      } catch (error) {
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.loadStaff, async (params) => {
      if (isEmpty(params?.storeCode)) {
        this.output.savedStaff.next([]);
        return;
      }
      try {
        const res = await this.airTimeRegistrationUseCase.getUserForFilter(
          params,
          "staff",
        );
        const raw = res ?? [];

        const allUsers = raw.flatMap((item: any) => item?.users ?? []);

        const mappedUsers: BaseUserResponseModel[] = allUsers.map((u: any) => {
          return {
            id: u.id,
            name: u.name || u.userName || u.email || u.phone || "",
            code: u.code,
          };
        });

        this.output.savedStaff.next(mappedUsers);
      } catch (e) {
        this.output.savedStaff.next([]);
        this.output.onError.next(e as Error);
      }
    });

    this.skipFirstInitial(this.input.loadUsers, async (params) => {
      const { providerCode } = params;
      try {
        let res;
        if (providerCode === "b2b") {
          res = await this.airTimeRegistrationUseCase.getUserForFilter(
            params,
            "userB2B",
          );
        } else if (providerCode === "mobitopup") {
          res = await this.airTimeRegistrationUseCase.getUserForFilter(
            params,
            "staff",
          );
        } else {
          res = await this.airTimeRegistrationUseCase.getUserForFilter(
            params,
            "userB2B",
          );
        }
        const raw = res ?? [];

        const allUsers = raw.flatMap((item: any) => item?.users ?? []);

        const mappedUsers: BaseUserResponseModel[] = allUsers.map((u: any) => {
          return {
            id: u.id,
            name: u.fullName || u.email || u.name || u.phone || "",
            code: u.code,
          };
        });

        this.output.savedUsers.next(mappedUsers);
      } catch (e) {
        this.output.savedUsers.next([]);
        this.output.onError.next(e as Error);
      }
    });

    this.skipFirstInitial(this.input.fetchDenomination, async (params) => {
      try {
        const { carrier, providerCode } = params;
        const query: DenominationListRequestEntity = {};

        if (providerCode) {
          query.providerCode = providerCode;
        }
        if (Array.isArray(carrier)) {
          carrier
            .filter((c) => !!c)
            .forEach((code, index) => {
              query[`carrier[${index}]`] = code;
            });
        }
        const res = await this.airTimeRegistrationUseCase.getFilterList(
          query,
          "denomination",
        );
        if (res) {
          this.output.savedDenominationList.next(res);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      }
    });
  }
}
